import React, { useState } from 'react';
import {
  Button,
  Popover,
  MenuList,
  MenuItem,
  Tooltip,
  Select,
  FormControl,
} from '@mui/material';
import { ChevronDown } from 'lucide-mui';
import {
  useOutputLanguage,
  OUTPUT_LANGUAGES,
} from '../hooks/useOutputLanguage';

interface OutputLanguageSelectProps {
  disabled?: boolean;
  variant?: 'button' | 'compact' | 'dictation';
}

const OutputLanguageSelect: React.FC<OutputLanguageSelectProps> = ({
  disabled = false,
  variant = 'button',
}) => {
  const {
    outputLanguage,
    currentLanguageName,
    loading,
    updateOutputLanguage,
  } = useOutputLanguage();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = async (language: string) => {
    setAnchorEl(null);
    await updateOutputLanguage(language);
  };

  // Handle Select onChange for dictation variant
  const handleSelectChange = async (event: any) => {
    await updateOutputLanguage(event.target.value);
  };

  // Render dictation-style select (matches mode dropdown)
  if (variant === 'dictation') {
    return (
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <Select
          value={outputLanguage}
          onChange={handleSelectChange}
          disabled={disabled || loading}
          sx={{
            borderRadius: '50px',
            backgroundColor: 'action.hover',
            border: 'none',
            '& .MuiSelect-select': {
              py: 1,
              borderRadius: '50px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                borderRadius: '12px',
                border: 'none',
                backgroundColor: 'action.hover',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                '& .MuiMenuItem-root': {
                  borderRadius: '8px',
                  mx: 1,
                  my: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.selected',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              },
            },
          }}
        >
          {OUTPUT_LANGUAGES.map((lang) => (
            <MenuItem key={lang.code} value={lang.code}>
              {lang.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Render button-style popover (original design)
  return (
    <>
      <Tooltip title="Select output language" arrow>
        <Button
          color="secondary"
          variant={variant === 'compact' ? 'text' : 'outlined'}
          onClick={handleClick}
          endIcon={<ChevronDown />}
          disabled={disabled || loading}
          size={variant === 'compact' ? 'small' : 'medium'}
        >
          {currentLanguageName}
        </Button>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList>
          {OUTPUT_LANGUAGES.map((lang) => (
            <MenuItem
              key={lang.code}
              selected={lang.code === outputLanguage}
              onClick={() => handleLanguageSelect(lang.code)}
            >
              {lang.name}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};

export default OutputLanguageSelect;
